/**
 * Background order-flow model (realism pass v2 §1). Background coins have NO price target: price emerges from flow.
 *  - Attention tiers: dead (~70%), slowcook (~22%, 4-8 alive), hot (~6%, supervisor keeps 2-3 alive).
 *  - attention in [0,1] drives the trade rate: rate = base * attention^1.5 (+ Hawkes self-excitation).
 *  - Hidden Markov flow regimes change buy probability / size mix: accumulation, markup, distribution, capitulation.
 *  - Order sizes are log-normal by wallet type (retail / degen / whale), not by market cap.
 * All randomness comes from the token's own seeded stream, advanced only by its own tick work.
 */
import type { Rng } from './rng';

export type Tier = 'dead' | 'slowcook' | 'hot' | 'faded' | 'mature';
export type Regime = 'accum' | 'markup' | 'distrib' | 'capit';
export type WalletType = 'retail' | 'degen' | 'whale';
export type Death = { mode: 'bleed' | 'dump' | 'fakepump'; until: number; dumped?: boolean };

export interface FlowState {
  tier: Tier;
  attention: number;
  halfLifeMs: number;
  base: number; // trades/s at attention 1
  regime: Regime; regimeUntil: number;
  hawkes: number; alpha: number; tauS: number; // self-excitation (trades/s), branching ratio, decay
  deadLeft: number; deadUntil: number; removeAt: number;
  death?: Death; diedAt?: number;
  lastMc: number; jumpCooldown: number;
  trendCooldown?: number; panicCooldown?: number;
}

export const FLOW = {
  tierShare: [['dead', 70], ['slowcook', 22], ['hot', 8]] as [Tier, number][],
  hotMin: 2, hotMax: 3, slowMin: 4, slowMax: 8,
  hotBase: [8, 40] as const, slowBase: [0.3, 2] as const,
  hotHalfLifeS: [20, 90] as const, slowHalfLifeS: [180, 600] as const,
  hotRegimeS: [5, 60] as const, slowRegimeS: [30, 300] as const,
  buyP: { accum: 0.515, markup: 0.56, distrib: 0.465, capit: 0.42 } as Record<Regime, number>,
  mix: { dead: [0.85, 0.15, 0], slowcook: [0.82, 0.175, 0.005], hot: [0.62, 0.36, 0.02], faded: [0.9, 0.1, 0], mature: [0.75, 0.24, 0.01] } as Record<Tier, [number, number, number]>,
  deathAttention: 0.15,
};

const r2 = (r: Rng, [a, b]: readonly [number, number]) => r.range(a, b);

export function newFlow(r: Rng, tier: Tier, t: number): FlowState {
  const f: FlowState = { tier, attention: 0, halfLifeMs: 0, base: 0, regime: 'accum', regimeUntil: t, hawkes: 0, alpha: r.range(0.4, 0.7), tauS: r.range(0.5, 2), deadLeft: 0, deadUntil: 0, removeAt: Infinity, lastMc: 0, jumpCooldown: 0 };
  setTier(r, f, tier, t);
  return f;
}

export function setTier(r: Rng, f: FlowState, tier: Tier, t: number) {
  f.tier = tier;
  if (tier === 'dead') { f.deadLeft = r.int(2, 15); f.deadUntil = t + r.range(5000, 40000); f.removeAt = t + r.range(60000, 120000); f.attention = 0.3; f.base = 0; }
  if (tier === 'slowcook') { f.attention = r.range(0.3, 0.6); f.base = r2(r, FLOW.slowBase); f.halfLifeMs = r2(r, FLOW.slowHalfLifeS) * 1000; f.removeAt = t + r.range(20, 40) * 60000; f.regime = 'accum'; f.regimeUntil = t + r2(r, FLOW.slowRegimeS) * 1000; }
  if (tier === 'hot') { f.attention = 1; f.base = r2(r, FLOW.hotBase); f.halfLifeMs = r2(r, FLOW.hotHalfLifeS) * 1000; f.removeAt = Infinity; f.regime = 'markup'; f.regimeUntil = t + r2(r, FLOW.hotRegimeS) * 1000; f.death = undefined; }
  if (tier === 'faded') { f.base = 0.15; f.attention = 0.2; f.removeAt = t + r.range(120000, 300000); }
  if (tier === 'mature') { f.base = 0.8; f.attention = 0.5; f.halfLifeMs = 900_000; f.removeAt = Infinity; f.regime = 'accum'; f.regimeUntil = t + 60000; }
}

/** Markov regime transition (hot coins switch faster and more violently). */
export function nextRegime(r: Rng, f: FlowState, t: number) {
  const hot = f.tier === 'hot';
  const T: Record<Regime, [Regime, number][]> = {
    accum: [['markup', hot ? 55 : 35], ['accum', 30], ['distrib', 20], ['capit', hot ? 10 : 5]],
    markup: [['markup', hot ? 30 : 20], ['distrib', 40], ['accum', 20], ['capit', hot ? 25 : 8]],
    distrib: [['capit', hot ? 35 : 20], ['accum', 35], ['markup', 25], ['distrib', 15]],
    capit: [['accum', 50], ['markup', hot ? 30 : 15], ['capit', 15], ['distrib', 10]],
  };
  f.regime = r.weighted(T[f.regime]);
  f.regimeUntil = t + r2(r, hot ? FLOW.hotRegimeS : FLOW.slowRegimeS) * 1000;
}

/** Trade arrival rate (trades/s) before Hawkes excitation. */
export function baseRate(f: FlowState, t: number): number {
  switch (f.tier) {
    case 'dead': return t < f.deadUntil && f.deadLeft > 0 ? Math.max(0.2, f.deadLeft / Math.max(1, (f.deadUntil - t) / 1000)) : 0;
    case 'slowcook': return f.base * Math.pow(f.attention / 0.4, 1.5);
    case 'hot': return f.death ? f.base * 0.35 : f.base * Math.pow(f.attention, 1.5);
    case 'faded': return 0.05;
    case 'mature': return f.base * Math.pow(f.attention / 0.5, 1.5);
  }
}

/** Attention: mean-reverting decay with noise (jumps are applied by the market on price action / KOL buys). */
export function stepAttention(r: Rng, f: FlowState, dtMs: number) {
  if (f.tier === 'hot') f.attention = Math.max(0, Math.min(1, f.attention * Math.pow(2, -dtMs / f.halfLifeMs) + (r.next() - 0.5) * 0.02));
  else if (f.tier === 'slowcook' || f.tier === 'mature') { const m = f.tier === 'mature' ? 0.5 : 0.4; f.attention = Math.max(0.05, Math.min(1, m + (f.attention - m) * Math.pow(2, -dtMs / f.halfLifeMs) + (r.next() - 0.5) * 0.04)); }
  f.hawkes *= Math.exp(-dtMs / 1000 / f.tauS);
}
export function jump(f: FlowState, amount: number) { f.attention = Math.min(1, f.attention + amount); }

/** Buy probability for the current regime / death mode. */
export function buyProb(f: FlowState, t: number): number {
  if (f.death) {
    if (f.death.mode === 'bleed') return 0.25;
    if (f.death.mode === 'fakepump') return t < f.death.until - 20000 ? 0.8 : 0.15;
    return 0.3;
  }
  if (f.tier === 'slowcook' || f.tier === 'mature') return FLOW.buyP[f.regime] + 0.02; // slight positive drift
  if (f.tier === 'faded') return 0.35;
  return FLOW.buyP[f.regime];
}

export function walletType(r: Rng, f: FlowState): WalletType {
  const [a, b] = FLOW.mix[f.tier]; const x = r.next();
  return x < a ? 'retail' : x < a + b ? 'degen' : 'whale';
}
/** Log-normal order size by wallet type (SOL). */
export function orderSize(r: Rng, w: WalletType): number {
  const n = gauss(r);
  if (w === 'retail') return clamp(0.3 * Math.exp(0.95 * n), 0.02, 5);
  if (w === 'degen') return clamp(1.5 * Math.exp(0.85 * n), 0.1, 20);
  return Math.exp(r.range(Math.log(10), Math.log(80)));
}
export function sniperSize(r: Rng) { return Math.exp(r.range(Math.log(0.5), Math.log(8))); }
function gauss(r: Rng) { const u = Math.max(1e-9, r.next()), v = r.next(); return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v); }
const clamp = (x: number, a: number, b: number) => Math.max(a, Math.min(b, x));
/** Poisson draw (Knuth for small lambda, normal approx above). */
export function poisson(r: Rng, lambda: number): number {
  if (lambda <= 0) return 0;
  if (lambda > 30) return Math.max(0, Math.round(lambda + Math.sqrt(lambda) * gauss(r)));
  const L = Math.exp(-lambda); let k = 0; let p = 1;
  do { k++; p *= r.next(); } while (p > L && k < 200);
  return k - 1;
}
